import React, { useState, useEffect, useMemo } from 'react';
import { Button, Table, Badge, Spinner, Alert, Card, Modal, Form, InputGroup } from 'react-bootstrap';
import Select from 'react-select';
import { workoutAPI } from '../services/workoutAPI';
import TemplateForm from './TemplateForm';

function TemplateManager({ onBack, onStartWorkout }) {
  const [templates, setTemplates] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Copy functionality state
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [templateToCopy, setTemplateToCopy] = useState(null);
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [isCopying, setIsCopying] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Search, sort, collapse state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [collapsedSections, setCollapsedSections] = useState({});

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [templatesData, programsData] = await Promise.all([
        workoutAPI.fetchAllTemplates(),
        workoutAPI.fetchPrograms()
      ]);
      setTemplates(templatesData);
      // Only show template programs (is_template = 1) for copy-to destination
      setPrograms(programsData.filter(p => p.is_template === 1 || p.is_template === true));
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Tokenized search
  const tokenize = (query) => query.toLowerCase().split(/\s+/).filter(Boolean);

  // Filter and sort templates
  const processedTemplates = useMemo(() => {
    let filtered = templates;

    // Apply search filter
    if (searchQuery.trim()) {
      const tokens = tokenize(searchQuery);
      filtered = templates.filter(t => {
        const searchable = `${t.name} ${t.description || ''} ${t.program_name || ''}`.toLowerCase();
        return tokens.every(token => searchable.includes(token));
      });
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aVal, bVal;
      if (sortColumn === 'name') {
        aVal = (a.name || '').toLowerCase();
        bVal = (b.name || '').toLowerCase();
      } else if (sortColumn === 'count') {
        aVal = a.exercise_count || 0;
        bVal = b.exercise_count || 0;
      }
      const cmp = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [templates, searchQuery, sortColumn, sortDirection]);

  // Group templates: Global first, then by program
  const groupedTemplates = useMemo(() => {
    const globalTemplates = processedTemplates.filter(t => !t.is_inline);
    const inlineTemplates = processedTemplates.filter(t => t.is_inline);

    // Group inline templates by program
    const byProgram = {};
    inlineTemplates.forEach(t => {
      const key = t.program_id;
      if (!byProgram[key]) {
        byProgram[key] = {
          program_id: t.program_id,
          program_name: t.program_name,
          templates: []
        };
      }
      byProgram[key].templates.push(t);
    });

    return {
      global: globalTemplates,
      programs: Object.values(byProgram)
    };
  }, [processedTemplates]);

  // Sort handlers
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Collapse handlers
  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const collapseAll = () => {
    const all = { global: true };
    groupedTemplates.programs.forEach(p => { all[p.program_id] = true; });
    setCollapsedSections(all);
  };

  const expandAll = () => setCollapsedSections({});

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setShowForm(true);
  };

  const handleEdit = async (template) => {
    try {
      const fullTemplate = await workoutAPI.fetchTemplateById(template.id);
      setEditingTemplate(fullTemplate);
      setShowForm(true);
    } catch (err) {
      console.error('Error fetching template details:', err);
      setError('Failed to load template details. Please try again.');
    }
  };

  const handleSave = async (formData) => {
    setError(null);
    try {
      if (editingTemplate) {
        await workoutAPI.updateTemplate(editingTemplate.id, formData);
      } else {
        await workoutAPI.createTemplate(formData);
      }
      setShowForm(false);
      setEditingTemplate(null);
      await fetchTemplates();
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Failed to save template. Please try again.');
    }
  };

  const handleDelete = async (templateId) => {
    if (!deleteConfirm || deleteConfirm !== templateId) {
      setDeleteConfirm(templateId);
      return;
    }

    setError(null);
    try {
      await workoutAPI.deleteTemplate(templateId);
      setDeleteConfirm(null);
      await fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Failed to delete template. Please try again.');
    }
  };

  const handleStartFromTemplate = async (template) => {
    try {
      const fullTemplate = await workoutAPI.fetchTemplateById(template.id);
      onStartWorkout(fullTemplate);
    } catch (err) {
      console.error('Error loading template:', err);
      setError('Failed to load template. Please try again.');
    }
  };

  const handleCopyToGlobal = async (template) => {
    setError(null);
    setIsCopying(true);
    try {
      const result = await workoutAPI.copyTemplate(template.id, null);
      setSuccessMessage(`Template "${result.name}" copied to Global Templates!`);
      await fetchTemplates();
    } catch (err) {
      console.error('Error copying template:', err);
      setError('Failed to copy template. Please try again.');
    } finally {
      setIsCopying(false);
    }
  };

  const handleCopyToProgram = (template) => {
    setTemplateToCopy(template);
    setSelectedProgramId(null);
    setShowCopyModal(true);
  };

  const handleConfirmCopyToProgram = async () => {
    if (!templateToCopy || !selectedProgramId) return;

    setError(null);
    setIsCopying(true);
    try {
      const targetProgram = programs.find(p => p.id === selectedProgramId);
      await workoutAPI.copyTemplate(templateToCopy.id, selectedProgramId);
      setSuccessMessage(`Template copied to program "${targetProgram?.name}"!`);
      setShowCopyModal(false);
      setTemplateToCopy(null);
      setSelectedProgramId(null);
      await fetchTemplates();
    } catch (err) {
      console.error('Error copying template:', err);
      setError('Failed to copy template. Please try again.');
    } finally {
      setIsCopying(false);
    }
  };

  // Render sortable header
  const SortableHeader = ({ column, label }) => (
    <th
      onClick={() => handleSort(column)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      title={`Sort by ${label}`}
    >
      {label}
      {sortColumn === column ? (
        <i className={`fas fa-chevron-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
      ) : (
        <i className="fas fa-chevron-down ms-1" style={{ opacity: 0.3 }}></i>
      )}
    </th>
  );

  // Render template row
  const TemplateRow = ({ template, isInline }) => (
    <tr key={template.id}>
      <td><strong>{template.name}</strong></td>
      <td>
        <span className="text-muted">
          {template.description || 'No description'}
        </span>
      </td>
      <td>
        <Badge bg="info">
          {template.exercise_count || 0} exercise{(template.exercise_count || 0) !== 1 ? 's' : ''}
        </Badge>
      </td>
      <td>
        <div className="btn-group" role="group">
          <Button
            variant="success"
            size="sm"
            onClick={() => handleStartFromTemplate(template)}
            title="Start workout from this template"
          >
            <i className="fas fa-play me-1"></i>
            Start
          </Button>
          {isInline ? (
            <Button
              variant="outline-info"
              size="sm"
              onClick={() => handleCopyToGlobal(template)}
              title="Copy to Global Templates"
              disabled={isCopying}
            >
              <i className="fas fa-upload me-1"></i>
              To Global
            </Button>
          ) : (
            <Button
              variant="outline-info"
              size="sm"
              onClick={() => handleCopyToProgram(template)}
              title="Copy to a program as inline template"
              disabled={programs.length === 0}
            >
              <i className="fas fa-copy me-1"></i>
              To Program
            </Button>
          )}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleEdit(template)}
            title="Edit template"
          >
            <i className="fas fa-edit"></i>
          </Button>
          <Button
            variant={deleteConfirm === template.id ? 'danger' : 'outline-danger'}
            size="sm"
            onClick={() => handleDelete(template.id)}
            title={deleteConfirm === template.id ? 'Click again to confirm' : 'Delete template'}
          >
            <i className={`fas ${deleteConfirm === template.id ? 'fa-exclamation-triangle' : 'fa-trash'}`}></i>
            {deleteConfirm === template.id && <span className="ms-1">Sure?</span>}
          </Button>
        </div>
      </td>
    </tr>
  );

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading templates...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading templates...</p>
      </div>
    );
  }

  const totalSections = 1 + groupedTemplates.programs.length;
  const hasResults = processedTemplates.length > 0;

  return (
    <div className="template-manager">
      <div className="d-flex justify-content-between align-items-center mb-4 p-1">
        <div>
          <h2>
            <i className="fas fa-file-alt me-2"></i>
            Workout Templates
          </h2>
          <p className="text-muted">Create and manage your workout templates</p>
        </div>
        <Button variant="outline-secondary" onClick={onBack}>
          <i className="fas fa-arrow-left me-2"></i>
          Back
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          <i className="fas fa-check-circle me-2"></i>
          {successMessage}
        </Alert>
      )}

      {/* Toolbar: Create + Search + Collapse Controls */}
      <div className="d-flex flex-wrap gap-2 mb-4 align-items-center">
        <Button variant="primary" onClick={handleCreateNew}>
          <i className="fas fa-plus me-2"></i>
          Create New Template
        </Button>

        <InputGroup style={{ maxWidth: '300px' }}>
          <InputGroup.Text>
            <i className="fas fa-search"></i>
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <Button variant="outline-secondary" onClick={() => setSearchQuery('')}>
              <i className="fas fa-times"></i>
            </Button>
          )}
        </InputGroup>

        {templates.length > 0 && totalSections > 1 && (
          <div className="btn-group">
            <Button variant="outline-secondary" size="sm" onClick={collapseAll} title="Collapse all sections">
              <i className="fas fa-compress-alt me-1"></i>
              Collapse All
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={expandAll} title="Expand all sections">
              <i className="fas fa-expand-alt me-1"></i>
              Expand All
            </Button>
          </div>
        )}

        {searchQuery && (
          <Badge bg="secondary" className="py-2 px-3">
            {processedTemplates.length} result{processedTemplates.length !== 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      {templates.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <i className="fas fa-file-alt fa-4x text-muted mb-3"></i>
            <h4>No Templates Yet</h4>
            <p className="text-muted">
              Create your first workout template to save time on your recurring workouts
            </p>
            <Button variant="primary" onClick={handleCreateNew}>
              <i className="fas fa-plus me-2"></i>
              Create Template
            </Button>
          </Card.Body>
        </Card>
      ) : !hasResults ? (
        <Card>
          <Card.Body className="text-center py-5">
            <i className="fas fa-search fa-3x text-muted mb-3"></i>
            <h4>No Matching Templates</h4>
            <p className="text-muted">
              No templates match "{searchQuery}"
            </p>
            <Button variant="outline-secondary" onClick={() => setSearchQuery('')}>
              Clear Search
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div className="templates-list">
          {/* Global Templates Section */}
          <Card className="mb-4">
            <Card.Header
              onClick={() => toggleSection('global')}
              style={{ cursor: 'pointer' }}
              className="d-flex justify-content-between align-items-center"
            >
              <div>
                <h5 className="mb-0">
                  <i className={`fas fa-chevron-${collapsedSections['global'] ? 'right' : 'down'} me-2`}></i>
                  <i className="fas fa-globe me-2"></i>
                  Global Templates
                  <Badge bg="primary" className="ms-2">{groupedTemplates.global.length}</Badge>
                </h5>
                <small className="text-muted">
                  Standalone templates available for any workout
                </small>
              </div>
            </Card.Header>
            {!collapsedSections['global'] && (
              <Card.Body className="p-0">
                {groupedTemplates.global.length > 0 ? (
                  <Table responsive hover className="mb-0">
                    <thead>
                      <tr>
                        <SortableHeader column="name" label="Name" />
                        <th>Description</th>
                        <SortableHeader column="count" label="Exercises" />
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedTemplates.global.map((template) => (
                        <TemplateRow key={template.id} template={template} isInline={false} />
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <i className="fas fa-info-circle me-2"></i>
                    {searchQuery ? 'No matching global templates' : 'No global templates yet'}
                  </div>
                )}
              </Card.Body>
            )}
          </Card>

          {/* Inline Templates by Program */}
          {groupedTemplates.programs.map((programGroup) => (
            <Card key={programGroup.program_id} className="mb-4">
              <Card.Header
                onClick={() => toggleSection(programGroup.program_id)}
                style={{ cursor: 'pointer' }}
                className="bg-light d-flex justify-content-between align-items-center"
              >
                <div>
                  <h5 className="mb-0">
                    <i className={`fas fa-chevron-${collapsedSections[programGroup.program_id] ? 'right' : 'down'} me-2`}></i>
                    <i className="fas fa-calendar-week me-2"></i>
                    {programGroup.program_name}
                    <Badge bg="secondary" className="ms-2">
                      {programGroup.templates.length} template{programGroup.templates.length !== 1 ? 's' : ''}
                    </Badge>
                    <Badge bg="warning" text="dark" className="ms-2">Inline</Badge>
                  </h5>
                  <small className="text-muted">
                    Templates scoped to this program
                  </small>
                </div>
              </Card.Header>
              {!collapsedSections[programGroup.program_id] && (
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0">
                    <thead>
                      <tr>
                        <SortableHeader column="name" label="Name" />
                        <th>Description</th>
                        <SortableHeader column="count" label="Exercises" />
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {programGroup.templates.map((template) => (
                        <TemplateRow key={template.id} template={template} isInline={true} />
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Copy to Program Modal */}
      <Modal show={showCopyModal} onHide={() => setShowCopyModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-copy me-2"></i>
            Copy Template to Program
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Copy <strong>"{templateToCopy?.name}"</strong> as an inline template to:
          </p>
          <Form.Group>
            <Form.Label>Select Program</Form.Label>
            <Select
              options={programs.map(p => ({ value: p.id, label: p.name }))}
              onChange={(option) => setSelectedProgramId(option?.value || null)}
              value={programs.find(p => p.id === selectedProgramId)
                ? { value: selectedProgramId, label: programs.find(p => p.id === selectedProgramId)?.name }
                : null}
              placeholder="Choose a program..."
              isClearable
            />
          </Form.Group>
          <small className="text-muted mt-2 d-block">
            The template will be copied into the selected program. Changes to either copy will not affect the other.
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCopyModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmCopyToProgram}
            disabled={!selectedProgramId || isCopying}
          >
            {isCopying ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Copying...
              </>
            ) : (
              <>
                <i className="fas fa-copy me-2"></i>
                Copy Template
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {showForm && (
        <TemplateForm
          show={showForm}
          onHide={() => {
            setShowForm(false);
            setEditingTemplate(null);
            setError(null);
          }}
          onSave={handleSave}
          template={editingTemplate}
          error={error}
        />
      )}
    </div>
  );
}

export default TemplateManager;
